import { AlertTriangle } from "lucide-react";
import { useMemo, useState } from "react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import { useFeeMutations } from "../../hooks/useFeeMutations";
import { PaymentFeeKind, type PaymentFee, type ProviderRailRoute } from "../../types/paymentFees.types";
import { corridorLabel, formatFeeFormula, scopeChipsFor } from "../../utils/feeFormat";
import { removalImpact } from "../../utils/feeMath";

export interface DeleteFeeDialogProps {
  fee: PaymentFee;
  open: boolean;
  onClose: () => void;
  /** `wasMissing` is true when the row was already gone (404) — the caller still refreshes, without an error. */
  onDeleted: (wasMissing: boolean) => void;
  fees: PaymentFee[];
  routes: ProviderRailRoute[];
  organizationNames: Record<string, string>;
}

/** Deleting is for correcting a mistake; the dialog says what each affected corridor falls back to. */
export function DeleteFeeDialog({
  fee,
  open,
  onClose,
  onDeleted,
  fees,
  routes,
  organizationNames,
}: DeleteFeeDialogProps) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  const { isSubmitting, error, removeFee } = useFeeMutations();

  const [now] = useState(() => Date.now());
  const impact = useMemo(() => removalImpact([fee], fees, routes, now), [fee, fees, routes, now]);
  const goingDark = impact.filter((item) => item.after == null);
  const chips = useMemo(
    () => scopeChipsFor(fee, labels.chips, routes, organizationNames),
    [fee, labels, routes, organizationNames],
  );

  const handleConfirm = async () => {
    const outcome = await removeFee(fee.id);
    if (outcome !== "error") onDeleted(outcome === "missing");
  };

  return (
    <ColocaAlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onClose();
      }}
      title={t("delete_title")}
      description={t("delete_semantics")}
      confirmLabel={t("delete_confirm")}
      confirmVariant="destructive"
      confirmDisabled={isSubmitting}
      cancelLabel={t("delete_cancel")}
      onConfirm={() => void handleConfirm()}
      onCancel={onClose}
    >
      <div className="space-y-3">
        <div className="space-y-2 rounded-xl border border-border bg-card p-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {chips.map((chip) => (
              <ColocaBadge
                key={chip.id}
                variant={
                  chip.id === "organization" && !chip.muted ? "primary" : chip.muted ? "secondary" : "default"
                }
                className={cn(
                  chip.muted && "border-dashed text-muted-foreground",
                  chip.mono ? "font-mono" : "font-sans",
                )}
              >
                {chip.label}
              </ColocaBadge>
            ))}
            <ColocaBadge variant="default" className="ml-auto text-muted-foreground">
              v{fee.version}
            </ColocaBadge>
          </div>
          <p className="font-mono text-sm tabular-nums">
            {formatFeeFormula(fee, activeLanguage, labels.formula)}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatDateTime(fee.valid_from, { locale: activeLanguage })}
          </p>
        </div>

        {impact.some((item) => item.after != null) && (
          <section className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("delete_after_title")}
            </h3>
            <ul className="space-y-1 text-sm">
              {impact
                .filter((item) => item.after != null)
                .map((item) => (
                  <li key={item.route.id} className="flex flex-wrap justify-between gap-2">
                    <span className="text-muted-foreground">
                      {corridorLabel(item.route, labels.directionShort)}
                    </span>
                    <span className="font-mono">
                      {formatFeeFormula(item.after!, activeLanguage, labels.formula)}
                    </span>
                  </li>
                ))}
            </ul>
          </section>
        )}

        {goingDark.length > 0 && (
          <ColocaAlert
            variant="destructive"
            icon={AlertTriangle}
            text={
              <span className="flex flex-col gap-2">
                <span>
                  {t(
                    fee.fee_kind === PaymentFeeKind.COLOCA_COMMISSION
                      ? "delete_escalate_commission"
                      : "delete_escalate_cost",
                    { count: goingDark.length },
                  )}
                </span>
                <span className="flex flex-wrap gap-1.5">
                  {goingDark.map((item) => (
                    <ColocaBadge key={item.route.id} variant="destructive" className="font-sans">
                      {corridorLabel(item.route, labels.directionShort)}
                    </ColocaBadge>
                  ))}
                </span>
              </span>
            }
          />
        )}

        {error && <ColocaAlert variant="destructive" text={error} />}
      </div>
    </ColocaAlertDialog>
  );
}
